import apiClient from './client';
import type { ReviewStats } from './spacedRepetitionService';

export interface StatsActivityItem {
    id: string;
    period: string;
    /** Missing from API builds before 2026-11-02. */
    days_ago?: number;
    type: string;
    added_count: number;
    reviewed_count: number;
    collections_created: number;
    collection_name?: string;
}

export interface WeeklyDayPoint {
    date: string;
    reviews: number;
    words_added: number;
    practice_sessions: number;
}

/** Response from GET /api/v1/stats/overview */
export interface StatsOverview {
    language: string;
    total_collections: number;
    total_words: number;
    words_mastered: number;
    words_in_progress: number;
    mastery_percent: number;
    words_added_this_week: number;
    words_added_prev_week: number;
    words_added_week_trend: number;
    practice_sessions_total: number;
    practice_sessions_this_week: number;
    review: ReviewStats;
    activity_items: StatsActivityItem[];
    weekly_chart: WeeklyDayPoint[];
    streak_days: number;
    active_days: number;
    today_sr_success_pct: number;
    today_practice_success_pct: number;
}

export interface PracticeSessionPayload {
    mode: 'quiz' | 'matching' | 'flashcards' | 'spelling' | 'typing' | 'fill_blank' | 'lesson';
    language: string;
    collection_id?: number | null;
    questions_total: number;
    correct: number;
    wrong: number;
    duration_seconds?: number;
    completed?: boolean;
    abandoned_at_question?: number;
    xp_earned?: number;
}

export interface HardestWord {
    vocabulary_id: number;
    word: string;
    translation: string[];
    total_reviews: number;
    failed_reviews: number;
    failure_rate_percent: number;
}

export interface RelearningRate {
    mastered_count: number;
    relearning_count: number;
    relearning_rate_percent: number;
}

export interface ReviewHistoryPoint {
    date: string;
    reviews: number;
    passed: number;
    failed: number;
}

export interface SessionCompletionByMode {
    mode: string;
    total: number;
    completed: number;
    abandoned: number;
    completion_rate_percent: number;
}

export interface EventCount {
    event_name: string;
    count: number;
    unique_users: number;
}

export interface EngagementStats {
    dau: number;
    wau: number;
    mau: number;
    event_counts: EventCount[];
}

export const statsService = {
    async getOverview(language: string): Promise<StatsOverview> {
        const { data } = await apiClient.get<StatsOverview>('/api/v1/stats/overview', {
            params: language ? { language } : {},
        });
        return data;
    },

    async logPracticeSession(payload: PracticeSessionPayload): Promise<void> {
        await apiClient.post('/api/v1/stats/practice-sessions', {
            mode: payload.mode,
            language: payload.language,
            collection_id: payload.collection_id ?? undefined,
            questions_total: payload.questions_total,
            correct: payload.correct,
            wrong: payload.wrong,
            duration_seconds: payload.duration_seconds,
            completed: payload.completed,
            abandoned_at_question: payload.abandoned_at_question,
            xp_earned: payload.xp_earned,
        });
    },

    async getHardestWords(limit: number = 10): Promise<HardestWord[]> {
        const { data } = await apiClient.get<{ words: HardestWord[] }>('/api/v1/stats/hardest-words', {
            params: { limit },
        });
        return data.words;
    },

    async getRelearningRate(): Promise<RelearningRate> {
        const { data } = await apiClient.get<RelearningRate>('/api/v1/stats/relearning-rate');
        return data;
    },

    async getReviewHistory(days: number = 30): Promise<ReviewHistoryPoint[]> {
        const { data } = await apiClient.get<{ history: ReviewHistoryPoint[] }>('/api/v1/stats/review-history', {
            params: { days },
        });
        return data.history;
    },

    async getHeatmapData(days: number = 90): Promise<ReviewHistoryPoint[]> {
        const { data } = await apiClient.get<{ history: ReviewHistoryPoint[] }>('/api/v1/stats/heatmap', {
            params: { days },
        });
        return data.history;
    },

    async getSessionCompletion(): Promise<SessionCompletionByMode[]> {
        const { data } = await apiClient.get<{ modes: SessionCompletionByMode[] }>('/api/v1/stats/session-completion');
        return data.modes;
    },

    async getEngagement(): Promise<EngagementStats> {
        const { data } = await apiClient.get<EngagementStats>('/api/v1/stats/engagement');
        return data;
    },
};
